'use client';

import { useState, useRef } from 'react';
import { useRouter } from '@/i18n/routing';
import { createFootprint, updateFootprint } from "@/lib/actions/footprints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Save, Upload, X } from "lucide-react";
import { Link } from "@/i18n/routing";

import { RichTextEditor } from "../shared/RichTextEditor";
import { MediaImg } from '@/components/shared/MediaImg';

interface FootprintFormProps {
  initialData?: any;
}

export function FootprintForm({ initialData }: FootprintFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEditing = !!initialData;

  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    slug: initialData?.slug || '',
    image: initialData?.image || '',
    description: initialData?.description || '',
    order: initialData?.order || 0,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'title' && !isEditing) {
        newData.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }
      return newData;
    });
  };

  const handleManualChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setIsUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('type', 'footprints');

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: uploadData,
        credentials: 'include',
      });

      const result = await response.json();

      if (result.success) {
        setFormData(prev => ({ ...prev, image: result.url }));
        toast.success('Image uploaded successfully');
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      toast.error('An error occurred during upload');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = isEditing 
        ? await updateFootprint(initialData.id, formData)
        : await createFootprint(formData);

      if (result.success) {
        toast.success(`Footprint ${isEditing ? 'updated' : 'created'} successfully`);
        router.push('/admin/footprints');
      } else {
        toast.error(result.error || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to save footprint");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href="/admin/footprints">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold text-navy">
          {isEditing ? 'Edit Footprint / Industry' : 'Add New Footprint / Industry'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Industry Title</Label>
                  <Input id="title" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Energy & Infrastructure" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug</Label>
                  <Input id="slug" name="slug" value={formData.slug} onChange={handleChange} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <RichTextEditor value={formData.description} onChange={(val) => handleManualChange('description', val)} />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Cover Image</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  {formData.image ? (
                    <div className="relative aspect-video rounded-lg border overflow-hidden group">
                      <MediaImg src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-video rounded-lg border-2 border-dashed border-light flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-light transition-all"
                    >
                      <Upload className="h-8 w-8 text-gray mb-2" />
                      <span className="text-xs text-gray font-medium text-center px-4">Click to upload industry cover image</span>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*"
                  />
                  
                  {isUploading && (
                    <p className="text-xs text-center text-gray animate-pulse">Uploading...</p>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="image">Image Path</Label>
                    <Input
                      id="image"
                      name="image"
                      value={formData.image}
                      onChange={handleChange}
                      placeholder="/upload/footprints/..."
                      className="text-xs"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Display Order</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="order">Display Order</Label>
                  <Input id="order" name="order" type="number" value={formData.order} onChange={handleChange} />
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-navy hover:bg-primary transition-all" disabled={isLoading}>
              {isLoading ? 'Saving...' : <><Save className="mr-2 h-4 w-4" /> Save Footprint</>}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
